import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChatBubbleLeftRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useStartThreadMutation } from '../../store/api/localApi';
import { ThreadAlreadyOpenError } from '../../types';
import { THREAD_BODY_MAX } from './ThreadView';

// ── StartThreadButton ────────────────────────────────────────────────────────
//
// The parent's "Message staff" entry point: a button that opens a composer
// scoped to one child.
//
// THE 409: a child may have only one OPEN thread at a time. When the parent
// tries to start a second, the backend answers 409 THREAD_ALREADY_OPEN with the
// id of the thread that already exists. That is NOT an error state for the
// parent — the conversation they want is already open, so we tell them where it
// went and carry them into it. Blocking them dead would be the wrong read of
// the anti-spam rule: it gates new-thread creation, not the parent's ability to
// be heard.

/** Narrows an RTK rejection to the 409 conflict body, or null for anything else. */
function asThreadAlreadyOpen(err: unknown): ThreadAlreadyOpenError | null {
  const rejection = err as { status?: number; data?: Partial<ThreadAlreadyOpenError> } | undefined;
  if (rejection?.status !== 409) return null;
  const data = rejection.data;
  if (data?.reason !== 'THREAD_ALREADY_OPEN' || typeof data.threadId !== 'string') return null;
  return data as ThreadAlreadyOpenError;
}

export interface StartThreadButtonProps {
  /** The child this conversation is about. */
  studentId: string;
  /**
   * Base path for thread routes, e.g. `/parent/<studentId>/threads`. A created
   * (or already-open) thread is opened at `${threadPathBase}/${threadId}`.
   */
  threadPathBase: string;
  /** Optional label override for the trigger button. */
  label?: string;
}

const StartThreadButton: React.FC<StartThreadButtonProps> = ({
  studentId,
  threadPathBase,
  label = 'Message staff',
}) => {
  const navigate = useNavigate();
  const [startThread, { isLoading }] = useStartThreadMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [body, setBody] = useState('');
  const [composeError, setComposeError] = useState<string | null>(null);
  const [alreadyOpenThreadId, setAlreadyOpenThreadId] = useState<string | null>(null);

  const trimmed = body.trim();
  const canSubmit = trimmed.length > 0 && trimmed.length <= THREAD_BODY_MAX && !isLoading;

  const openThread = (threadId: string) => {
    navigate(`${threadPathBase}/${threadId}`);
  };

  const closeComposer = () => {
    setIsOpen(false);
    setComposeError(null);
    setAlreadyOpenThreadId(null);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit) return;
    setComposeError(null);
    setAlreadyOpenThreadId(null);

    try {
      const { threadId } = await startThread({ studentId, body: trimmed }).unwrap();
      setBody('');
      setIsOpen(false);
      openThread(threadId);
    } catch (err) {
      const conflict = asThreadAlreadyOpen(err);
      if (conflict) {
        // Not a dead end — this parent already has a conversation running about
        // this child, so surface the explanation and take them straight to it.
        setAlreadyOpenThreadId(conflict.threadId);
        openThread(conflict.threadId);
        return;
      }
      setComposeError('Could not send your message. Please try again.');
    }
  };

  return (
    <div>
      {!isOpen && (
        <button
          type="button"
          data-testid={`thread-start-btn-${studentId}`}
          onClick={() => setIsOpen(true)}
          className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
        >
          <ChatBubbleLeftRightIcon className="h-4 w-4" />
          {label}
        </button>
      )}

      {isOpen && (
        <form
          onSubmit={handleSubmit}
          data-testid={`thread-compose-${studentId}`}
          className="bg-white rounded-lg shadow p-4 space-y-3"
        >
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold text-gray-900">{label}</h2>
            <button
              type="button"
              data-testid="thread-compose-cancel"
              onClick={closeComposer}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Cancel"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <label htmlFor="thread-compose-body" className="sr-only">
            Your message
          </label>
          <textarea
            id="thread-compose-body"
            data-testid="thread-compose-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={4}
            maxLength={THREAD_BODY_MAX}
            placeholder="What would you like to ask the team?"
            className="block w-full rounded-md border-gray-300 shadow-sm text-sm focus:border-primary-500 focus:ring-primary-500"
          />

          <div className="flex items-center justify-between gap-3">
            <span className="text-xs text-gray-400">
              {trimmed.length}/{THREAD_BODY_MAX}
            </span>
            <button
              type="submit"
              data-testid="thread-compose-submit"
              disabled={!canSubmit}
              className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Sending…' : 'Send'}
            </button>
          </div>

          {alreadyOpenThreadId && (
            <p
              data-testid={`thread-already-open-${studentId}`}
              className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800"
            >
              You already have a conversation open about this child — taking you there so you can
              add to it.
            </p>
          )}

          {composeError && (
            <p data-testid="thread-compose-error" className="text-sm text-red-600">
              {composeError}
            </p>
          )}
        </form>
      )}
    </div>
  );
};

export default StartThreadButton;
